import React from 'react';
import { Link } from 'react-router-dom';
import {
	ArrowRightIcon,
	BanknotesIcon,
	BoltIcon,
	CurrencyDollarIcon,
	Battery100Icon,
} from '@heroicons/react/24/outline';
import SunRaysHero from './Services/SunRaysHero';

function Eyebrow({ children, dark = false }) {
	return (
		<p
			className={`text-xs font-semibold uppercase tracking-[0.18em] ${
				dark ? 'text-primary-400' : 'text-primary-600'
			}`}
		>
			<span className='inline-block w-8 h-px align-middle mr-3 bg-current opacity-60' />
			{children}
		</p>
	);
}

// Maryland Solar Incentives — one of the six highest-value pages. Copy is Gabe's,
// kept plain and non-promissory ("may qualify", "may include") on purpose.
const incentives = [
	{
		title: 'Federal Tax Credit',
		icon: CurrencyDollarIcon,
		body: 'Eligible homeowners may qualify for federal tax incentives when purchasing a solar system.',
	},
	{
		title: 'SRECs',
		icon: BanknotesIcon,
		body: 'Solar Renewable Energy Credits allow homeowners to earn additional income based on solar production.',
	},
	{
		title: 'Net Metering',
		icon: BoltIcon,
		body: 'Maryland homeowners can receive utility bill credits for excess electricity sent to the grid.',
	},
	{
		title: 'Battery Incentives',
		icon: Battery100Icon,
		body: 'Additional programs and incentives may periodically become available for battery storage systems.',
	},
];

export default function MarylandIncentives() {
	return (
		<div className='bg-surface text-ink-900 antialiased'>
			<section className='relative isolate overflow-hidden border-b border-ink-100'>
				<SunRaysHero seed={7} />
				<div className='mx-auto max-w-7xl px-6 py-20 sm:py-28 lg:px-8'>
					<Eyebrow>Incentives & savings</Eyebrow>
					<h1 className='font-display mt-5 text-5xl font-medium tracking-tight text-ink-900 sm:text-6xl leading-[1.05]'>
						Maryland solar incentives.
					</h1>
					<p className='mt-6 max-w-2xl text-lg leading-8 text-ink-600'>
						Maryland continues to be one of the best states in the country for
						residential solar. Here are the incentives that can apply to your
						project.
					</p>
				</div>
			</section>

			<section className='bg-white py-20 sm:py-28'>
				<div className='mx-auto max-w-7xl px-6 lg:px-8'>
					<div className='grid grid-cols-1 gap-6 md:grid-cols-2'>
						{incentives.map((i) => (
							<div
								key={i.title}
								className='sg-card p-8'
							>
								<div className='sg-badge'>
									<i.icon
										className='h-5 w-5'
										aria-hidden='true'
									/>
								</div>
								<h2 className='font-display mt-6 text-2xl font-medium text-ink-900'>
									{i.title}
								</h2>
								<p className='mt-3 text-base leading-7 text-ink-500'>{i.body}</p>
							</div>
						))}
					</div>
				</div>
			</section>

			<section className='bg-surface border-y border-ink-100 py-20'>
				<div className='mx-auto max-w-3xl px-6 text-center lg:px-8'>
					<h2 className='font-display text-3xl font-medium tracking-tight text-ink-900 sm:text-4xl leading-tight'>
						I help homeowners understand which incentives they qualify for — and
						how to maximize their savings.
					</h2>
					<div className='mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-4'>
						<Link
							to='/contact'
							className='inline-flex items-center gap-2 rounded-full bg-primary-500 px-8 py-4 text-base font-semibold text-white shadow-lg shadow-primary-500/25 hover:bg-primary-600 transition-all'
						>
							See what you qualify for
							<ArrowRightIcon className='h-5 w-5' />
						</Link>
						<Link
							to='/resources'
							className='text-sm font-semibold text-ink-700 hover:text-primary-600'
						>
							or explore the resource center
						</Link>
					</div>
					<p className='mt-8 text-xs leading-5 text-ink-400'>
						Incentive availability and eligibility change with federal and state
						policy. Talk with a tax professional about how any credit applies to
						your situation.
					</p>
				</div>
			</section>
		</div>
	);
}
